import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Send, Loader2 } from 'lucide-react';
import CommentRow from './CommentNode';
import { useAuth } from '../context/AuthContext';
import { getTierBadge, effectivePlan } from '../utils/tierBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const CLIENT_ID_KEY = 'outlaw_client_id';
const getClientId = () => {
  if (typeof window === 'undefined') return null;
  let id = window.localStorage.getItem(CLIENT_ID_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
         `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(CLIENT_ID_KEY, id);
  }
  return id;
};

/**
 * Convert a flat list of comments into a pre-ordered flat list with depth,
 * so the renderer never needs to recurse. Children of a comment are appended
 * immediately after the parent, in the order they were created.
 */
const flattenThreads = (comments) => {
  const byId = new Map();
  const childrenOf = new Map();
  comments.forEach((c) => { byId.set(c.id, c); });
  comments.forEach((c) => {
    const pid = c.parent_id && byId.has(c.parent_id) ? c.parent_id : '__root__';
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid).push(c);
  });
  const flat = [];
  const walk = (node, depth) => {
    flat.push({ ...node, __depth: depth });
    const kids = childrenOf.get(node.id) || [];
    for (const k of kids) walk(k, depth + 1);
  };
  const roots = childrenOf.get('__root__') || [];
  for (const r of roots) walk(r, 0);
  return flat;
};

const ArticleEngagement = ({ articleId, adminToken = null }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ likes: 0, comments: 0, user_liked: false });
  const [busy, setBusy] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [authorName, setAuthorName] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [clientId] = useState(() => getClientId());

  const [activeReplyId, setActiveReplyId] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyAuthor, setReplyAuthor] = useState('');
  const [submittingReplyFor, setSubmittingReplyFor] = useState(null);

  // Persist author name across sessions - only used by signed-out visitors.
  // (Post-grace, anon commenting is blocked server-side anyway, but we keep
  // the local persistence so the field remembers a chosen alias during
  // grace.)
  useEffect(() => {
    if (user) return;
    const saved = window.localStorage.getItem('outlaw_comment_author');
    if (saved) {
      setAuthorName(saved);
      setReplyAuthor(saved);
    }
  }, [user]);

  const loadStats = useCallback(async () => {
    if (!articleId) return;
    try {
      const url = `${API}/api/article-engagement/stats/${articleId}${clientId ? `?client_id=${clientId}` : ''}`;
      const res = await fetch(url);
      if (res.ok) setStats(await res.json());
    } catch { /* silent */ }
  }, [articleId, clientId]);

  const loadComments = useCallback(async () => {
    if (!articleId) return;
    setLoadingComments(true);
    try {
      const url = `${API}/api/article-engagement/comments/${articleId}${clientId ? `?client_id=${clientId}` : ''}`;
      const res = await fetch(url);
      if (res.ok) setComments(await res.json());
    } catch { /* silent */ }
    finally { setLoadingComments(false); }
  }, [articleId, clientId]);

  useEffect(() => { loadStats(); loadComments(); }, [loadStats, loadComments]);

  const flatThreads = useMemo(() => flattenThreads(comments), [comments]);

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    setStats((s) => ({
      ...s,
      likes: s.user_liked ? Math.max(0, s.likes - 1) : s.likes + 1,
      user_liked: !s.user_liked,
    }));
    try {
      const res = await fetch(`${API}/api/article-engagement/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ article_id: articleId, client_id: clientId }),
      });
      if (res.ok) {
        const data = await res.json();
        setStats((s) => ({ ...s, likes: data.likes, user_liked: data.liked }));
      } else {
        loadStats();
      }
    } catch { loadStats(); }
    finally { setBusy(false); }
  };

  const postComment = async ({ text, name, parentId }) => {
    const res = await fetch(`${API}/api/article-engagement/comment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        article_id: articleId,
        author_name: (name || '').trim() || 'Anon',
        comment_text: text.trim(),
        client_id: clientId,
        parent_id: parentId || null,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Comment failed. Try again.');
    }
    return res.json();
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const nu = await postComment({ text: commentText, name: authorName, parentId: null });
      setComments((c) => [...c, nu]);
      setCommentText('');
      window.localStorage.setItem('outlaw_comment_author', authorName.trim());
      loadStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentId) => {
    setSubmittingReplyFor(parentId);
    setError('');
    try {
      const nu = await postComment({ text: replyDraft, name: replyAuthor, parentId });
      setComments((c) => [...c, nu]);
      setReplyDraft('');
      if (replyAuthor.trim()) {
        window.localStorage.setItem('outlaw_comment_author', replyAuthor.trim());
      }
      setActiveReplyId(null);
      loadStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingReplyFor(null);
    }
  };

  const toggleCommentLike = async (comment) => {
    setComments((cs) =>
      cs.map((c) =>
        c.id === comment.id
          ? { ...c, likes: c.user_liked ? Math.max(0, c.likes - 1) : c.likes + 1, user_liked: !c.user_liked }
          : c,
      ),
    );
    try {
      const res = await fetch(`${API}/api/article-engagement/comment-like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment_id: comment.id, client_id: clientId }),
      });
      if (res.ok) {
        const data = await res.json();
        setComments((cs) =>
          cs.map((c) => (c.id === comment.id ? { ...c, likes: data.likes, user_liked: data.liked } : c)),
        );
      } else {
        loadComments();
      }
    } catch { loadComments(); }
  };

  const deleteComment = async (comment) => {
    if (!adminToken) return;
    if (!window.confirm('Delete this comment and all its replies?')) return;
    try {
      const res = await fetch(`${API}/api/article-engagement/comment/${comment.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        loadComments();
        loadStats();
      }
    } catch { /* silent */ }
  };

  return (
    <section className="mt-10" data-testid="article-engagement">
      <div className="flex items-center gap-4 flex-wrap border-t border-zinc-800 pt-6">
        <button
          type="button"
          onClick={toggleLike}
          disabled={busy}
          data-testid="article-like-btn"
          aria-pressed={stats.user_liked}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-black text-sm uppercase tracking-widest transition-all ${
            stats.user_liked
              ? 'bg-red-600 border-red-500 text-white shadow-[0_0_18px_-4px_rgba(220,38,38,0.9)]'
              : 'bg-zinc-950 border-zinc-800 text-gray-300 hover:border-red-500/60 hover:text-red-300'
          } disabled:opacity-70`}
        >
          <Heart className={`h-4 w-4 ${stats.user_liked ? 'fill-white' : ''}`} />
          <span data-testid="article-like-count">{stats.likes}</span>
          <span className="text-[10px] font-black">{stats.user_liked ? 'Liked' : 'Like'}</span>
        </button>

        <div className="inline-flex items-center gap-2 text-sm text-gray-400 font-bold uppercase tracking-widest">
          <MessageCircle className="h-4 w-4 text-red-400" />
          <span data-testid="article-comment-count">{stats.comments}</span>
          <span>Comments</span>
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid="article-comments-list">
        {loadingComments ? (
          <div className="text-gray-500 text-sm inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading comments…
          </div>
        ) : flatThreads.length === 0 ? (
          <p className="text-gray-500 text-sm">Be the first to speak up.</p>
        ) : (
          flatThreads.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              depth={c.__depth}
              onToggleLike={toggleCommentLike}
              onReply={submitReply}
              onDelete={deleteComment}
              activeReplyId={activeReplyId}
              setActiveReplyId={setActiveReplyId}
              replyDraft={replyDraft}
              setReplyDraft={setReplyDraft}
              replyAuthor={replyAuthor}
              setReplyAuthor={setReplyAuthor}
              submittingReplyFor={submittingReplyFor}
              adminToken={adminToken}
              user={user}
            />
          ))
        )}
      </div>

      <form onSubmit={submitComment} className="mt-6 rounded-lg border border-red-900/40 bg-zinc-950/70 p-4" data-testid="article-comment-form">
        <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Add a comment</label>
        {user ? (
          <div
            data-testid="article-comment-identity"
            className="mt-2 flex items-center gap-3 rounded border border-zinc-800 bg-black/40 px-3 py-2"
          >
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="h-8 w-8 rounded-full object-cover border border-zinc-700 shrink-0"
                data-testid="article-comment-identity-avatar"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-xs font-black text-red-400 shrink-0">
                {(user.display_name || 'O').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Posting as</p>
              {(() => {
                const badge = getTierBadge(effectivePlan(user));
                return (
                  <p
                    className={'text-sm font-black truncate ' + badge.nameClass}
                    style={badge.nameStyle || undefined}
                    data-testid="article-comment-identity-name"
                    title={badge.label}
                  >
                    {badge.emoji ? (
                      <span aria-hidden className={'mr-1 ' + badge.emojiClass}>
                        {badge.emoji}
                      </span>
                    ) : null}
                    {user.display_name || 'Outlaw Reader'}
                  </p>
                );
              })()}
            </div>
            <Link
              to="/account"
              data-testid="article-comment-identity-change"
              className="text-[10px] font-black uppercase tracking-[0.2em] text-red-400 hover:text-red-300 shrink-0"
            >
              Change →
            </Link>
          </div>
        ) : (
          <input
            type="text"
            maxLength={60}
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Name (or leave blank for Anon)"
            data-testid="article-comment-author"
            className="w-full mt-2 bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        )}
        <textarea
          maxLength={1000}
          required
          rows={3}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Your take on this story…"
          data-testid="article-comment-text"
          className="w-full mt-2 bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none resize-y"
        />
        {error && <p className="mt-2 text-red-400 text-xs" data-testid="article-comment-error">{error}</p>}
        <div className="mt-2 flex items-center justify-between">
          <p className="text-[10px] text-gray-500 uppercase tracking-widest">{commentText.length}/1000</p>
          <button
            type="submit"
            disabled={submitting || !commentText.trim()}
            data-testid="article-comment-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 text-white text-[11px] font-black uppercase tracking-widest"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Post Comment
          </button>
        </div>
      </form>
    </section>
  );
};

export default ArticleEngagement;
